import React, { useState, useEffect } from "react";
import { Line } from "react-chartjs-2";
import AthleteLayout from "../common/AthleteLayout";
import { useAthleteAuthContext } from "../login/useAthleteAuthContext";
import { useNavigate, useParams } from "react-router-dom";
import { AthleteHeader } from "../common/AthleteHeader";
import API_URL from "../../api/API";
import "chart.js/auto";
import bgImg from "../../images/runRocket.jpeg";


const ArrowD = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="14"
    height="8"
    viewBox="0 0 14 8"
    fill="none"
  >
    <path
      d="M13 1L7 7L1 1"
      stroke="black"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const AthleteRunrocketPerformance = () => {
  const { athlete } = useAthleteAuthContext();
  const { template_id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [performanceData, setPerformanceData] = useState({});
  const [openIndex, setOpenIndex] = useState(null);

  const trainingFocuses = [
    "Technical",
    "Speed - Strength",
    "Power",
    "Strength - Speed",
  ];

  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        const data = {};
        for (const focus of trainingFocuses) {
          const response = await fetch(
            API_URL.GET_ATHLETE_RUNROCKET_PERFORMANCE +
              athlete.id +
              "/" +
              focus,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${athlete.token}`,
              },
            },
          );
          if (!response.ok)
            throw new Error("Failed to fetch " + focus + " data");
          const result = await response.json();
          data[focus] = result.data; // { "Week 1": [{day, speed_data}, ...], ... }
        }
        setPerformanceData(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    if (athlete?.id && athlete?.token) fetchPerformanceData();
  }, [athlete.id, athlete.token]);

  // 🔹 Combine ALL weeks + days into one continuous line per focus
  const prepareChartData = (focus) => {
    if (!performanceData[focus]) return { labels: [], datasets: [] };

    const labels = [];
    const values = [];

    Object.keys(performanceData[focus]).forEach((weekName) => {
      const weekMatch = weekName.match(/Week\s*(\d+)/i);
      const weekNumber = weekMatch ? weekMatch[1] : "";

      performanceData[focus][weekName].forEach((dayObj) => {
        if (Array.isArray(dayObj.speed_data) && dayObj.speed_data.length > 0) {
          // ✅ Compute MAX speed of that day
          const maxSpeed = Math.max(...dayObj.speed_data.map(Number));

          // Use short day label (e.g., Mon, Tue)
          const shortDay = dayObj.day ? dayObj.day.slice(0, 3) : "";

          labels.push(`W${weekNumber} - ${shortDay}`);
          values.push(maxSpeed);
        }
      });
    });

    return {
      labels,
      datasets: [
        {
          label: `${focus} Max Speed`,
          data: values,
          borderColor: "#c19e42",
          backgroundColor: "rgba(30,136,229,0.15)",
          tension: 0.25,
          pointRadius: 4,
          pointHoverRadius: 6,
        },
      ],
    };
  };

  const chartOptions = {
    responsive: true,
    plugins: { legend: { display: true } },
    scales: {
      x: { ticks: { autoSkip: false, maxRotation: 45, minRotation: 45 } },
      y: { beginAtZero: true, title: { display: true, text: "Speed" } },
    },
  };

  const toggleAccordion = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <AthleteLayout>
      <div className="relative h-full bg-white pb-5 overflow-y-auto">
        <div className="w-full max-w-screen-xl mx-auto border-t border-black/30 mt-2"></div>

        {/* background image if need again */}
        {/* <div className="absolute inset-0">
          <img src={bgImg} alt="bgImg" className="object-cover w-full h-full" />
          <div className="w-full h-full absolute inset-0 bg-white/70"></div>
        </div> */}
        <div className="relative w-full max-w-2xl mx-auto pt-5 px-5">
          <div className="bg-black rounded-2xl flex justify-between gap-8 px-4 py-4 mb-5">
            <span
              onClick={() => navigate("/athlete/tools/runrocket/test/result")}
              className="text-white/40 text-sm xs:text-base cursor-pointer"
            >
              Test
            </span>
            <span
              onClick={() =>
                navigate(
                  "/athlete/tools/runrocket/training-weeks/" + template_id,
                )
              }
              className="text-white/40 text-sm xs:text-base cursor-pointer hover:text-white transition"
            >
              Train
            </span>
            <span className="text-white text-sm xs:text-base cursor-pointer hover:text-white transition">
              Performance
            </span>
          </div>
        </div>

        <div className="relative w-full max-w-2xl mx-auto space-y-5 px-5 mb-8">
          {trainingFocuses.map((focus, index) => (
            <div
              key={index}
              // className={`relative border-b border-black/20 last:border-none ${
              //   openIndex === index && "!border-none"
              // }`}
            >
              <div
                className={`${
                  openIndex === index && "!border-black/30"
                }  border border-black rounded-xl flex flex-col bg-white cursor-pointer`}
              >
                <button
                  className="w-full flex justify-between items-center gap-5 text-white text-base font-bold bg-black text-left px-4 py-3 rounded-lg"
                  onClick={() => toggleAccordion(index)}
                >
                  <span>{focus.replace(/([A-Z])/g, " $1").trim()}</span>
                  <span className={openIndex === index ? "rotate-180" : ""}>
                    <ArrowD />
                  </span>
                </button>

                {openIndex === index && (
                  <div className="pt-[15px] pb-3 px-3">
                    {isLoading ? (
                      <p className="pt-3">Loading...</p>
                    ) : error ? (
                      <p className="text-red-500 pt-3">{error}</p>
                    ) : (
                      <Line
                        data={prepareChartData(focus)}
                        options={chartOptions}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AthleteLayout>
  );
};

export default AthleteRunrocketPerformance;
